import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Upload, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';
import { useCreateDispute, DisputeType } from '../../../hooks/useDisputes';

// Image compression via canvas
async function compressImage(file: File, maxWidth = 1920, quality = 0.8): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('Canvas not supported')); return; }
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(
        (blob) => {
          if (!blob) { reject(new Error('Compression failed')); return; }
          const compressed = new File([blob], file.name, { type: 'image/jpeg' });
          resolve(compressed);
        },
        'image/jpeg',
        quality
      );
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = url;
  });
}

// Currency mask helper
function formatCurrency(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function parseCurrencyInput(value: string): number {
  const cleaned = value.replace(/[^\d]/g, '');
  return parseInt(cleaned || '0', 10);
}

export function DisputeCreate() {
  const navigate = useNavigate();
  const createDispute = useCreateDispute();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [amountCents, setAmountCents] = useState(0);
  const [claimedDate, setClaimedDate] = useState('');
  const [claimedType, setClaimedType] = useState<DisputeType>('DEPOSIT');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);

  const handleFileSelect = useCallback(async (selectedFile: File | null) => {
    if (!selectedFile) return;
    try {
      const compressed = await compressImage(selectedFile);
      setFile(compressed);
      setPreview(URL.createObjectURL(compressed));
      setErrors((prev) => ({ ...prev, file: '' }));
    } catch {
      setErrors((prev) => ({ ...prev, file: 'Erro ao processar imagem' }));
    }
  }, []);

  const handleAmountChange = (value: string) => {
    const cents = parseCurrencyInput(value);
    setAmountCents(cents);
    setErrors((prev) => ({ ...prev, amount: '' }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!file) newErrors.file = 'Envie o comprovante de pagamento';
    if (amountCents < 100) newErrors.amount = 'Valor mínimo é R$ 1,00';
    if (!claimedDate) newErrors.date = 'Informe a data do pagamento';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate() || !file) return;
    try {
      await createDispute.mutateAsync({
        file,
        claimedAmountCents: amountCents,
        claimedDate,
        claimedType,
      });
      setSuccess(true);
      setTimeout(() => navigate('/banking/disputes'), 2000);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      setErrors({ submit: error?.response?.data?.message || 'Erro ao enviar contestação. Tente novamente.' });
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Contestação Enviada!</h2>
          <p className="text-gray-600 dark:text-gray-400">Sua contestação foi registrada e será analisada em breve.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
          <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
        </button>
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">Contestar Pagamento</h1>
      </div>

      {/* Image Upload */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Comprovante de Pagamento
        </label>
        {preview ? (
          <div className="relative">
            <img src={preview} alt="Comprovante" className="w-full rounded-lg border border-gray-200 dark:border-gray-700 max-h-64 object-contain bg-gray-50 dark:bg-gray-900" />
            <button
              onClick={() => { setFile(null); setPreview(null); }}
              className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 text-xs hover:bg-red-600"
            >
              ✕
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => cameraInputRef.current?.click()}
              className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-primary-500 dark:hover:border-primary-400 transition-colors"
            >
              <Camera className="w-8 h-8 text-gray-400 dark:text-gray-500" />
              <span className="text-sm text-gray-600 dark:text-gray-400">Tirar Foto</span>
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-primary-500 dark:hover:border-primary-400 transition-colors"
            >
              <Upload className="w-8 h-8 text-gray-400 dark:text-gray-500" />
              <span className="text-sm text-gray-600 dark:text-gray-400">Escolher Arquivo</span>
            </button>
          </div>
        )}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => handleFileSelect(e.target.files?.[0] || null)}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFileSelect(e.target.files?.[0] || null)}
        />
        {errors.file && <p className="text-sm text-red-500">{errors.file}</p>}
      </div>

      {/* Amount */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Valor do Pagamento
        </label>
        <input
          type="text"
          inputMode="numeric"
          placeholder="R$ 0,00"
          value={amountCents > 0 ? formatCurrency(amountCents) : ''}
          onChange={(e) => handleAmountChange(e.target.value)}
          className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-lg font-medium focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        {errors.amount && <p className="text-sm text-red-500">{errors.amount}</p>}
      </div>

      {/* Date */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Data do Pagamento
        </label>
        <input
          type="date"
          value={claimedDate}
          onChange={(e) => { setClaimedDate(e.target.value); setErrors((prev) => ({ ...prev, date: '' })); }}
          max={new Date().toISOString().split('T')[0]}
          className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
        {errors.date && <p className="text-sm text-red-500">{errors.date}</p>}
      </div>

      {/* Type Toggle */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Tipo de Operação
        </label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setClaimedType('DEPOSIT')}
            className={`px-4 py-3 rounded-lg font-medium text-sm transition-colors ${
              claimedType === 'DEPOSIT'
                ? 'bg-primary-500 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            Depósito
          </button>
          <button
            type="button"
            onClick={() => setClaimedType('TRANSFER')}
            className={`px-4 py-3 rounded-lg font-medium text-sm transition-colors ${
              claimedType === 'TRANSFER'
                ? 'bg-primary-500 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            Transferência
          </button>
        </div>
      </div>

      {/* Error */}
      {errors.submit && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <p className="text-sm text-red-700 dark:text-red-400">{errors.submit}</p>
        </div>
      )}

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={createDispute.isPending}
        className="w-full py-4 rounded-lg bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-base flex items-center justify-center gap-2 transition-colors"
      >
        {createDispute.isPending ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Enviando...
          </>
        ) : (
          'Enviar Contestação'
        )}
      </button>
    </div>
  );
}

export default DisputeCreate;
